<template>
  <div>
    <div class="text-center mb-5">
      <h1>Bienvenue à Dlibr</h1>
      <h2 class="lead h4 text-primary mb-5">
        Identification de l'usager
      </h2>
      <b-row>
        <b-col cols="12" lg="6">
          <div
            class="rounded bg-light d-flex flex-column align-items-center py-3"
          >
            <b-iconstack font-scale="5" class="mb-3">
              <b-icon stacked icon="person-circle" variant="info"></b-icon>
              <b-icon
                stacked
                icon="award-fill"
                variant="primary"
                scale="0.5"
                shift-h="7"
                shift-v="5"
              ></b-icon>
            </b-iconstack>
            <b-btn
              :to="{
                name: 'auth.login',
                query: { redirect: this.$route.query.redirect }
              }"
              pill
              variant="info"
              class="text-white stretched-link"
              >Je suis un enseignant</b-btn
            >
          </div>
        </b-col>
        <b-col cols="12" lg="6">
          <div
            class="rounded bg-light d-flex flex-column align-items-center py-3"
          >
            <b-iconstack font-scale="5" class="mb-3">
              <b-icon stacked icon="people-fill" variant="info"></b-icon>
              <b-icon
                stacked
                icon="bookmark-fill"
                variant="primary"
                scale="0.5"
                shift-h="7"
                shift-v="5"
              ></b-icon>
            </b-iconstack>
            <b-btn
              :to="{
                name: 'auth.login.students',
                query: { redirect: this.$route.query.redirect }
              }"
              pill
              variant="info"
              class="text-white d-block stretched-link"
            >
              Je suis un étudiant
            </b-btn>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
/**
 * @file       Login options component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */
import { required, minLength, email } from "vuelidate/lib/validators";
import { mapActions, mapState } from "vuex";

/**
 * Export component.
 * 
 * @type  {Object}
 */
export default {
  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      form: {
        email: null,
        password: null
      }
    };
  },

  /**
   * Vuelidate form validations.
   * 
   * @type  {Object}
   */
  validations: {
    form: {
      email: {
        required,
        email
      },
      password: {
        required,
        minLength: minLength(5)
      }
    }
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      authStatus: state => state.auth.profile.status
    })
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    ...mapActions("auth", {
      login: "login"
    }),

    /**
     * Sets the form value.
     *
     * @param      {string}  key     The new value
     * @param      {string}  value   The value
     */
    setValue(key, value) {
      this.form[key] = value;
      this.$v.form[key].$touch();
    },

    /**
     * Validate the form state.
     *
     * @param      {string}  name    The input name
     *
     * @return     {bool}  Dirty or not.
     */
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },

    /**
     * Resets the form.
     */
    resetForm() {
      this.form = {
        email: null,
        password: null
      };

      // Reset on next tick
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },

    /**
     * Called on submit.
     */
    onSubmit() {
      this.$v.form.$touch();

      // If any error found, stop the submit
      if (this.$v.form.$anyError) {
        return;
      }

      // Sends the login request
      this.login({
        ...this.form,
        username: this.form.email
      })
        .then(() => {
          // Display a success message
          this.$root.$bvToast.toast("Bienvenue", {
            title: "Connexion établie avec succès."
          });

          // Redirects to the admin dashboard
          this.$router.push({ name: "admin.users" });
        })
        .catch(() => {
          // Display an error message
          this.$root.$bvToast.toast("Valeur de connexion invalide", {
            title: "Erreur",
            variant: "danger"
          });
          this.resetForm();
        });
    }
  }
};
</script>
